import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { ChatViewComponent } from '../ai/components/chat-view/chat-view.component';
import { ThemeService } from '../admin-core/services/theme.service';

/**
 * Global floating Pebbl AI chat bubble.
 *
 * DESIGN — ported 1:1 from gatherco-console-v2 (src/app/ai-popup): draggable
 * sparkle FAB with a first-load intro ripple, a scrim below the header, and the
 * gold ✦ Pebbl BETA panel head with v2's two head actions: ⤢ opens the full /ai page
 * ({@link openFullPage}) and ✕ closes the widget. On phones the panel itself goes
 * full screen — CSS only, see the 640px breakpoint in the component stylesheet.
 *
 * FUNCTIONALITY — unchanged from the primary console: the panel embeds the real,
 * fully-functional {@link ChatViewComponent} (composer, SSE stream, tables,
 * feedback, markdown, model select). Every API call still originates there.
 * activeId=null mints a fresh conversation on the first message; *ngIf="isOpen"
 * destroys/recreates the chat on each open so every session starts clean.
 *
 * ViewEncapsulation.None (like AiComponent) so the shared ai.styles.css
 * (`.pebbl-ai` scope) styles the embedded chat.
 */
@Component({
  selector: 'app-ai-chat-widget',
  standalone: true,
  imports: [CommonModule, ChatViewComponent],
  templateUrl: './ai-chat-widget.component.html',
  styleUrls: ['../ai/ai.styles.css', './ai-chat-widget.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AiChatWidgetComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);
  private router = inject(Router);

  /** The embedded chat, so the full-page jump can carry its ongoing conversation. */
  @ViewChild(ChatViewComponent) private chatView?: ChatViewComponent;

  /** Whether the chat panel is open. Toggling it recreates a fresh conversation. */
  isOpen = false;

  /** Plays the pebble-drop intro animation on load. */
  introActive = false;

  /** Colour scheme — mirrors the global header theme (ThemeService). */
  theme: 'dark' | 'light' = 'light';

  /**
   * Edge-docked tab instead of the free-floating circle (console-v2's updated
   * launcher, `dockTrial` there). The circle could be dragged anywhere and sat
   * ON TOP of content; the tab hugs the right edge (ChatGPT-style) and only
   * drags vertically. Roll back by flipping this to false — the old FAB, its
   * drag logic and CSS are all still here.
   */
  readonly dockTrial = true;
  dockY: number | null = null;
  private dockDragging = false;
  private dockMoved = false;
  private dockOffsetY = 0;
  private static readonly TAB_H = 58;

  // ── Draggable FAB — null means the default bottom-right position ──
  fabX: number | null = null;
  fabY: number | null = null;
  private dragging = false;
  private dragMoved = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private static readonly FAB_SIZE = 56;
  private static readonly EDGE_GAP = 16;
  private static readonly HEADER_HEIGHT = 58;

  ngOnInit(): void {
    // Theme follows the global header toggle (ThemeService), so the widget always
    // opens in the console-wide light/dark choice and updates live while open.
    this.theme = this.themeService.theme;
    this.themeService.theme$.subscribe((t) => {
      this.theme = t;
      this.cdr.markForCheck();
    });

    // Intro on every load. To play once per session, gate this with sessionStorage.
    this.introActive = true;
    setTimeout(() => {
      this.introActive = false;
      this.cdr.markForCheck();
    }, 5200);

    // Restore the saved dock height, clamped to the current viewport.
    const savedDock = localStorage.getItem('pebblDockY');
    if (savedDock) {
      const y = Number(savedDock);
      this.dockY = Number.isFinite(y) ? this.clampDockY(y) : null;
    }

    // Restore the saved FAB position, clamped to the current viewport.
    const saved = localStorage.getItem('pebblFabPos');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        this.fabX = this.clampX(pos.x);
        this.fabY = this.clampY(pos.y);
      } catch {
        localStorage.removeItem('pebblFabPos');
      }
    }
  }

  // ── Dock tab drag — vertical only, hugs the right edge (view-only) ──
  onDockPointerDown(event: PointerEvent): void {
    this.dockDragging = true;
    this.dockMoved = false;
    const target = event.currentTarget as HTMLElement;
    this.dockOffsetY = event.clientY - target.getBoundingClientRect().top;
    target.setPointerCapture(event.pointerId);
  }

  private clampDockY(y: number): number {
    return Math.min(Math.max(y, AiChatWidgetComponent.HEADER_HEIGHT + 8),
      window.innerHeight - AiChatWidgetComponent.TAB_H - AiChatWidgetComponent.EDGE_GAP);
  }

  // ── FAB drag (view-only; no API involvement) ──
  onFabPointerDown(event: PointerEvent): void {
    this.dragging = true;
    this.dragMoved = false;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dragOffsetX = event.clientX - rect.left;
    this.dragOffsetY = event.clientY - rect.top;
    target.setPointerCapture(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.dockDragging) {
      const y = event.clientY - this.dockOffsetY;
      if (this.dockY === null || Math.abs(y - this.dockY) > 4) {
        this.dockMoved = true;
      }
      this.dockY = this.clampDockY(y);
      return;
    }
    if (!this.dragging) return;
    const x = event.clientX - this.dragOffsetX;
    const y = event.clientY - this.dragOffsetY;
    if (this.fabX === null || Math.abs(x - this.fabX) > 4 || Math.abs(y - (this.fabY ?? 0)) > 4) {
      this.dragMoved = true;
    }
    this.fabX = this.clampX(x);
    this.fabY = this.clampY(y);
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    if (this.dockDragging) {
      this.dockDragging = false;
      if (this.dockMoved && this.dockY !== null) {
        localStorage.setItem('pebblDockY', String(Math.round(this.dockY)));
      }
      return;
    }
    if (!this.dragging) return;
    this.dragging = false;
    if (this.dragMoved && this.fabX !== null && this.fabY !== null) {
      // Snap to the nearest horizontal edge, messenger-style.
      const mid = window.innerWidth / 2;
      this.fabX = this.fabX + AiChatWidgetComponent.FAB_SIZE / 2 < mid
        ? AiChatWidgetComponent.EDGE_GAP
        : window.innerWidth - AiChatWidgetComponent.FAB_SIZE - AiChatWidgetComponent.EDGE_GAP;
      localStorage.setItem('pebblFabPos', JSON.stringify({ x: this.fabX, y: this.fabY }));
    }
  }

  private clampX(x: number): number {
    return Math.min(Math.max(x, AiChatWidgetComponent.EDGE_GAP),
      window.innerWidth - AiChatWidgetComponent.FAB_SIZE - AiChatWidgetComponent.EDGE_GAP);
  }

  private clampY(y: number): number {
    return Math.min(Math.max(y, AiChatWidgetComponent.HEADER_HEIGHT + 8),
      window.innerHeight - AiChatWidgetComponent.FAB_SIZE - AiChatWidgetComponent.EDGE_GAP);
  }

  // ── Open/close (unchanged behaviour) ──
  toggle(): void {
    if (this.dragMoved || this.dockMoved) {
      this.dragMoved = false; // A drag just ended — don't treat it as a click.
      this.dockMoved = false;
      return;
    }
    if (!this.isOpen) this.theme = this.themeService.theme;
    this.isOpen = !this.isOpen;
    this.introActive = false; // Stop the intro as soon as the user engages.
  }

  close(): void {
    this.isOpen = false;
  }

  /**
   * Leave the widget for the full /ai page. If a chat is already under way, carry
   * its conversation across (the widget's ChatViewComponent self-manages the
   * session, so its `activeId` is the minted conversation id once the first
   * message is sent) — instead of dumping the user on a brand-new chat.
   */
  openFullPage(): void {
    const id = this.chatView?.activeId;
    this.router.navigate(id ? ['/ai/pebbl', id] : ['/ai']);
    this.close();
  }
}
